import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import {
  completePersonalAppointment,
  listPersonalAppointments,
  PersonalAppointmentDetail,
} from '../../api/personalAppointments';
import { PersonalAppointmentStatus } from '../../types/lead';
import { useAuth } from '../../context/authContext';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';

const STATUS_LABELS: Record<PersonalAppointmentStatus, string> = {
  SCHEDULED: 'Scheduled',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

/**
 * Lead -> Personal Appointment -> Application -> Documents -> Payment ->
 * Student workflow correction: a browsable list of every PA a Counselor
 * (their own) or Lead Manager/Branch Manager/Super Admin (everyone's) is
 * responsible for, so completing one doesn't require remembering which
 * lead it was created from.
 */
export const PersonalAppointmentsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canComplete = hasPermission('personal_appointments', 'complete');

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [status, setStatus] = useState<PersonalAppointmentStatus | ''>('SCHEDULED');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['personal-appointments', paginationModel.page, paginationModel.pageSize, status],
    queryFn: () =>
      listPersonalAppointments({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        status: status || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const completeMutation = useMutation({
    mutationFn: (id: number) => completePersonalAppointment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['personal-appointments'] }),
    onError: () => setError('Unable to complete this Personal Appointment.'),
  });

  const columns: GridColDef<PersonalAppointmentDetail>[] = useMemo(
    () => [
      { field: 'leadNumber', headerName: 'Lead #', width: 110, valueGetter: (_v, row) => row.lead.leadNumber },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        valueGetter: (_v, row) => `${row.lead.firstName} ${row.lead.lastName}`,
      },
      { field: 'phone', headerName: 'Phone', width: 140, valueGetter: (_v, row) => row.lead.phone },
      {
        field: 'assignedTo',
        headerName: 'Counselor',
        width: 160,
        valueGetter: (_v, row) =>
          row.lead.assignedTo ? `${row.lead.assignedTo.firstName} ${row.lead.assignedTo.lastName}` : '—',
      },
      { field: 'branch', headerName: 'Branch', width: 130, valueGetter: (_v, row) => row.lead.branch?.name ?? '—' },
      {
        field: 'appointmentDate',
        headerName: 'Appointment',
        width: 170,
        valueGetter: (_v, row) => (row.appointmentDate ? new Date(row.appointmentDate).toLocaleString() : '—'),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={STATUS_LABELS[params.row.status]}
            color={params.row.status === 'COMPLETED' ? 'success' : params.row.status === 'CANCELLED' ? 'default' : 'warning'}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        renderCell: (params) =>
          params.row.status === 'SCHEDULED' && canComplete ? (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                completeMutation.mutate(params.row.id);
              }}
            >
              Complete
            </Button>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canComplete],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={EventAvailableOutlinedIcon}
        color="#2F6FED"
        title="Personal Appointments"
        subtitle="Leads converted to a Personal Appointment — complete one to unlock Student conversion."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Status"
          sx={{ width: 180 }}
          value={status}
          onChange={(e) => setStatus(e.target.value as PersonalAppointmentStatus | '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="SCHEDULED">Scheduled</MenuItem>
          <MenuItem value="COMPLETED">Completed</MenuItem>
          <MenuItem value="CANCELLED">Cancelled</MenuItem>
        </TextField>
      </Stack>

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={query.data?.data ?? []}
          columns={columns}
          rowCount={query.data?.meta.total ?? 0}
          loading={query.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          slots={{ toolbar: TableToolbar }}
          onRowClick={(params) => navigate(`/leads/${params.row.lead.id}`)}
        />
      </Box>
    </Box>
  );
};
